import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import SettingsButton from '../../components/SettingsButton';
import SocialButton from '../../components/SocialButton';
import HelpButton from '../../components/HelpButton';
import GameLog from '../../components/GameLog';
import { useTheme } from '../../contexts/ThemeContext';
import { useSessionComms } from '../../contexts/SessionCommsContext';
import { sessionApi } from '../../services/sessionApi';
import getButtonStyle from '../../utils/getButtonStyle';
import storageManager from '../../utils/StorageManager';
import { buildInitialTrioGameState, cloneTrioGameState } from '../../utils/trioState';
import { getSessionPlayerNames } from '../../utils/sessionPlayers';
import {
    consumeSuppressedSessionWrite,
    getVirtualSnapshotSignature,
    markRemoteVirtualSnapshotApplied,
    markVirtualSnapshotSignature
} from '../../utils/virtualGameSnapshotSync';
import {
    canUseCachedGameStateInContext,
    clearSessionGameStateCaches,
    stampSessionScopedGameState,
} from '../../utils/sessionGameState';

const TRIO_MIN_PLAYERS = 3;
const TRIO_MAX_PLAYERS = 6;
const TRIO_GAME_CACHE_KEY = 'trio_game_state';
const TRIO_CPU_DIFFICULTY_PROFILES = {
    easy: {
        pairSeekChance: 0.2,
        trioChaseChance: 0.35,
        thinkMinMs: 3000,
        thinkMaxMs: 7000,
        revealMinMs: 1300,
        revealMaxMs: 2200,
    },
    medium: {
        pairSeekChance: 0.65,
        trioChaseChance: 0.75,
        thinkMinMs: 7000,
        thinkMaxMs: 12000,
        revealMinMs: 1600,
        revealMaxMs: 2600,
    },
    hard: {
        pairSeekChance: 0.9,
        trioChaseChance: 0.95,
        thinkMinMs: 5000,
        thinkMaxMs: 15000,
        revealMinMs: 1400,
        revealMaxMs: 2400,
    },
};

const normalizePlayerName = (value) => String(value || '').trim();
const isCpuName = (value) => /^cpu\s+\d+$/i.test(normalizePlayerName(value));

const randomBetween = (min, max) => {
    const safeMin = Math.max(0, Number(min) || 0);
    const safeMax = Math.max(safeMin, Number(max) || safeMin);
    return Math.floor(Math.random() * (safeMax - safeMin + 1)) + safeMin;
};

const TRIO_NUMBER_COLOR_MAP = {
    1: '#2E5BFF',
    2: '#3F73FF',
    3: '#5A8CFF',
    4: '#17A673',
    5: '#1FB87F',
    6: '#2DCA8C',
    7: '#E3B323',
    8: '#E59A1F',
    9: '#E67E22',
    10: '#D84A3A',
    11: '#C63D2F',
    12: '#A93226'
};

function getReadableTextColor(hexColor) {
    const normalized = String(hexColor || '').replace('#', '');
    if (normalized.length !== 6) return '#FFFFFF';
    const red = parseInt(normalized.slice(0, 2), 16);
    const green = parseInt(normalized.slice(2, 4), 16);
    const blue = parseInt(normalized.slice(4, 6), 16);
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
    return brightness > 160 ? '#111111' : '#FFFFFF';
}

function TrioNumberCard({ value, hidden = false, theme, onClick, disabled = false, compact = false }) {
    const isInteractive = typeof onClick === 'function';
    const isBlocked = isInteractive && disabled;
    const cardColor = TRIO_NUMBER_COLOR_MAP[value] || theme.accent;
    const textColor = getReadableTextColor(cardColor);
    const width = compact ? '38px' : '52px';
    const height = compact ? '52px' : '72px';
    const borderRadius = compact ? '8px' : '10px';
    const fontSize = compact ? '0.95rem' : '1.15rem';

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isBlocked || !isInteractive}
            className="reveal-card"
            style={{
                width,
                height,
                borderRadius,
                border: hidden ? `2px solid ${theme.light}` : `2px solid ${cardColor}`,
                background: hidden
                    ? `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.bgDark} 100%)`
                    : cardColor,
                color: hidden ? theme.textPrimary : textColor,
                fontWeight: 'bold',
                fontSize,
                fontFamily: 'inherit',
                display: 'grid',
                placeItems: 'center',
                cursor: isBlocked || !isInteractive ? 'default' : 'pointer',
                opacity: isBlocked ? 0.65 : 1,
                boxShadow: hidden ? `inset 0 0 0 1px ${theme.bgLight}` : 'none'
            }}
        >
            {hidden ? (
                <span style={{ fontSize: '0.72rem', letterSpacing: '0.08em' }}>TRIO</span>
            ) : value}
        </button>
    );
}

function TrioGame() {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { session, activeSessionContext, emitRealtimeEvent, virtualGameSyncTick } = useSessionComms();
    const isMobile = window.innerWidth <= 768;
    const isSessionMode = Boolean(activeSessionContext?.code);
    const sessionCode = String(activeSessionContext?.code || '').trim().toUpperCase();
    const sessionAlias = String(activeSessionContext?.alias || '').trim();
    const isSessionHost = isSessionMode
        && String(session?.hostAlias || '').trim().toLowerCase() === sessionAlias.toLowerCase();
    const sessionGame = isSessionMode && session?.activeVirtualGame?.game === 'trio'
        ? session.activeVirtualGame
        : null;
    const initialSessionSnapshot = sessionGame?.snapshot || location.state?.__sessionGameSnapshot || null;

    const cachedGameStateRaw = storageManager.getTemporaryData(TRIO_GAME_CACHE_KEY) || {};
    const shouldUseCachedTrioState = canUseCachedGameStateInContext(cachedGameStateRaw, isSessionMode, sessionCode);
    const hasCachedTrioState = Boolean(cachedGameStateRaw && Object.keys(cachedGameStateRaw).length);
    const cachedGameState = shouldUseCachedTrioState ? cachedGameStateRaw : {};
    const hasRouteState = Array.isArray(location.state?.players) && location.state.players.length > 0;
    const setupState = hasRouteState
        ? location.state
        : (sessionGame?.setup || cachedGameState.setup || {});
    const configuredPlayers = Array.isArray(setupState.players) ? setupState.players : [];
    const players = configuredPlayers.length >= TRIO_MIN_PLAYERS && configuredPlayers.length <= TRIO_MAX_PLAYERS
        ? configuredPlayers
        : [setupState.humanPlayerName || 'Player 1', 'CPU 1', 'CPU 2'];
    const targetTrios = setupState.targetTrios || 2;
    const difficulty = setupState.difficulty || 'easy';
    const humanPlayerName = setupState.humanPlayerName || players[0];
    const configuredHumans = Array.isArray(setupState.humanPlayers)
        ? setupState.humanPlayers.map(normalizePlayerName).filter(Boolean)
        : [];
    const sessionHumanPlayers = useMemo(
        () => (isSessionMode ? getSessionPlayerNames(session, sessionAlias) : []),
        [isSessionMode, session, sessionAlias]
    );

    const [state, setState] = useState(() => (
        initialSessionSnapshot?.state
            ? cloneTrioGameState(initialSessionSnapshot.state)
            : (hasRouteState || !cachedGameState.state
                ? buildInitialTrioGameState(players, targetTrios)
                : cachedGameState.state)
    ));
    const [currentPlayerIndex, setCurrentPlayerIndex] = useState(
        Number.isInteger(initialSessionSnapshot?.currentPlayerIndex)
            ? initialSessionSnapshot.currentPlayerIndex
            : (hasRouteState ? 0 : (cachedGameState.currentPlayerIndex ?? 0))
    );
    const [reveals, setReveals] = useState(
        Array.isArray(initialSessionSnapshot?.reveals)
            ? initialSessionSnapshot.reveals
            : (hasRouteState ? [] : (cachedGameState.reveals || []))
    );
    const [status, setStatus] = useState(
        typeof initialSessionSnapshot?.status === 'string'
            ? initialSessionSnapshot.status
            : (hasRouteState ? 'Reveal two cards. Match to continue.' : (cachedGameState.status || 'Reveal two cards. Match to continue.'))
    );
    const [logEntries, setLogEntries] = useState(
        Array.isArray(initialSessionSnapshot?.logEntries)
            ? initialSessionSnapshot.logEntries
            : (hasRouteState ? [] : (cachedGameState.logEntries || []))
    );
    const [winner, setWinner] = useState(
        initialSessionSnapshot?.winner ?? (hasRouteState ? null : (cachedGameState.winner || null))
    );
    const [isCpuActing, setIsCpuActing] = useState(false);
    const [isMobileTableModalOpen, setIsMobileTableModalOpen] = useState(false);
    const [hasPulledSessionSnapshot, setHasPulledSessionSnapshot] = useState(false);
    const cpuTimersRef = useRef([]);
    const revealsRef = useRef(reveals);
    const snapshotSignatureRef = useRef('');
    const suppressNextSessionWriteRef = useRef(false);
    const lastOutboundWriteAtRef = useRef(0);
    const lastAppliedRemoteUpdatedAtRef = useRef(0);
    const wasSessionModeRef = useRef(isSessionMode);

    useEffect(() => {
        if (shouldUseCachedTrioState) return;
        if (!hasCachedTrioState) return;
        storageManager.removeTemporaryData(TRIO_GAME_CACHE_KEY);
    }, [hasCachedTrioState, shouldUseCachedTrioState]);

    const resolvedHumanPlayers = useMemo(() => {
        const sourceNames = [
            ...configuredHumans,
            ...sessionHumanPlayers,
            ...(isSessionMode ? [] : [humanPlayerName]),
        ];

        const deduped = [];
        const seen = new Set();
        sourceNames.forEach((name) => {
            const normalized = normalizePlayerName(name);
            if (!normalized) return;
            const key = normalized.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            deduped.push(normalized);
        });

        if (deduped.length) return deduped;

        // Fallback for legacy/stale setup payloads: treat non-CPU seats as humans.
        const fromHands = Array.isArray(state?.hands)
            ? state.hands
                .map((hand) => normalizePlayerName(hand?.name))
                .filter((name) => name && !isCpuName(name))
            : [];

        if (fromHands.length) return fromHands;
        return [normalizePlayerName(humanPlayerName)].filter(Boolean);
    }, [configuredHumans, humanPlayerName, isSessionMode, sessionHumanPlayers, state?.hands]);

    const humanPlayerNameSet = useMemo(() => (
        new Set(resolvedHumanPlayers.map((name) => name.toLowerCase()))
    ), [resolvedHumanPlayers]);

    const isHumanSeat = (name) => {
        const normalized = normalizePlayerName(name).toLowerCase();
        if (!normalized) return false;
        if (humanPlayerNameSet.has(normalized)) return true;
        if (!humanPlayerNameSet.size) return !isCpuName(name);
        return false;
    };

    const hasBots = players.some((name) => !isHumanSeat(name));
    const isMultiHumanMode = resolvedHumanPlayers.length > 1;

    const resolvedViewerName = useMemo(() => {
        if (!isSessionMode) return humanPlayerName;
        const aliasLower = sessionAlias.toLowerCase();
        const fromHands = state.hands?.find((hand) => String(hand?.name || '').toLowerCase() === aliasLower)?.name;
        if (fromHands) return fromHands;
        const fromPlayers = players.find((name) => String(name || '').toLowerCase() === aliasLower);
        if (fromPlayers) return fromPlayers;
        return humanPlayerName;
    }, [humanPlayerName, isSessionMode, players, sessionAlias, state.hands]);

    const currentPlayer = state.hands[currentPlayerIndex]?.name;
    const isHumanTurn = Boolean(currentPlayer) && isHumanSeat(currentPlayer) && !winner;
    const isCpuTurn = !winner && Boolean(currentPlayer) && !isHumanSeat(currentPlayer);
    const isViewerTurn = Boolean(currentPlayer) && String(currentPlayer).toLowerCase() === String(resolvedViewerName).toLowerCase() && !winner;
    const isSessionHydrated = !isSessionMode || Boolean(initialSessionSnapshot?.state) || hasPulledSessionSnapshot;
    const canInteractTurn = isSessionMode ? (isSessionHydrated && isViewerTurn) : isHumanTurn;
    const visibleHandPlayerName = isSessionMode
        ? resolvedViewerName
        : (isMultiHumanMode ? currentPlayer : humanPlayerName);

    const localSessionSnapshot = useMemo(() => ({
        state,
        currentPlayerIndex,
        reveals,
        status,
        logEntries,
        winner,
        isCpuActing,
    }), [state, currentPlayerIndex, reveals, status, logEntries, winner, isCpuActing]);

    useEffect(() => {
        if (configuredPlayers.length < TRIO_MIN_PLAYERS || configuredPlayers.length > TRIO_MAX_PLAYERS) {
            storageManager.removeTemporaryData(TRIO_GAME_CACHE_KEY);
            navigate('/trio-setup');
        }
    }, [configuredPlayers.length, navigate]);

    useEffect(() => {
        markVirtualSnapshotSignature(snapshotSignatureRef, localSessionSnapshot);
    }, [localSessionSnapshot]);

    useEffect(() => {
        setHasPulledSessionSnapshot(false);
    }, [isSessionMode, sessionCode]);

    const applyRemoteSnapshotIfChanged = (snapshot, remoteUpdatedAt) => {
        if (!snapshot || !snapshot.state) return false;
        const remoteUpdatedMs = Number.isFinite(Date.parse(remoteUpdatedAt || ''))
            ? Date.parse(remoteUpdatedAt || '')
            : 0;
        if (remoteUpdatedMs && remoteUpdatedMs <= lastAppliedRemoteUpdatedAtRef.current) {
            return false;
        }
        if (remoteUpdatedMs && remoteUpdatedMs < (lastOutboundWriteAtRef.current - 250)) {
            // Ignore stale server echoes that predate our most recent local write.
            return false;
        }

        const nextSnapshot = {
            state: snapshot.state,
            currentPlayerIndex: Number.isInteger(snapshot.currentPlayerIndex) ? snapshot.currentPlayerIndex : 0,
            reveals: Array.isArray(snapshot.reveals) ? snapshot.reveals : [],
            status: typeof snapshot.status === 'string' ? snapshot.status : 'Waiting for host actions...',
            logEntries: Array.isArray(snapshot.logEntries) ? snapshot.logEntries : [],
            winner: snapshot.winner ?? null,
            isCpuActing: Boolean(snapshot.isCpuActing),
        };
        const incomingSignature = getVirtualSnapshotSignature(nextSnapshot);
        if (incomingSignature && incomingSignature === snapshotSignatureRef.current) {
            setHasPulledSessionSnapshot(true);
            return false;
        }

        markRemoteVirtualSnapshotApplied(snapshotSignatureRef, suppressNextSessionWriteRef, nextSnapshot);
        setState(cloneTrioGameState(nextSnapshot.state));
        setCurrentPlayerIndex(nextSnapshot.currentPlayerIndex);
        setReveals(nextSnapshot.reveals);
        setStatus(nextSnapshot.status);
        setLogEntries(nextSnapshot.logEntries);
        setWinner(nextSnapshot.winner);
        setIsCpuActing(nextSnapshot.isCpuActing);
        setHasPulledSessionSnapshot(true);
        if (remoteUpdatedMs) {
            lastAppliedRemoteUpdatedAtRef.current = remoteUpdatedMs;
        }
        return true;
    };

    useEffect(() => {
        if (!isSessionMode) return;
        const snapshot = sessionGame?.snapshot || location.state?.__sessionGameSnapshot;
        applyRemoteSnapshotIfChanged(snapshot, sessionGame?.updatedAt);
    }, [isSessionMode, location.state, sessionGame?.snapshot, sessionGame?.updatedAt]);

    useEffect(() => {
        if (!isSessionMode || !session) return;
        if (sessionGame) return;
        clearSessionGameStateCaches(sessionCode);
        navigate('/trio-setup', { replace: true });
    }, [isSessionMode, navigate, session, sessionCode, sessionGame]);

    useEffect(() => {
        if (wasSessionModeRef.current && !isSessionMode) {
            clearSessionGameStateCaches(sessionCode);
            navigate('/trio-setup', { replace: true });
        }
        wasSessionModeRef.current = isSessionMode;
    }, [isSessionMode, navigate, sessionCode]);

    useEffect(() => {
        if (!isSessionMode || !sessionCode) return undefined;

        let cancelled = false;
        const pull = async () => {
            try {
                const data = await sessionApi.getVirtualGameState(sessionCode);
                const state = data?.state;
                if (cancelled || !state || state.game !== 'trio' || !state.snapshot?.state) return;

                applyRemoteSnapshotIfChanged(state.snapshot, state.updatedAt);
            } catch {
                // no-op
            }
        };

        pull().catch(() => null);
        const interval = setInterval(() => {
            pull().catch(() => null);
        }, 1000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [isSessionMode, sessionCode]);

    useEffect(() => {
        if (!isSessionMode || !sessionCode || !virtualGameSyncTick) return;
        sessionApi.getVirtualGameState(sessionCode).then((data) => {
            const state = data?.state;
            if (!state || state.game !== 'trio' || !state.snapshot?.state) return;
            applyRemoteSnapshotIfChanged(state.snapshot, state.updatedAt);
        }).catch(() => null);
    }, [isSessionMode, sessionCode, virtualGameSyncTick]);

    useEffect(() => {
        if (!isSessionMode || !sessionCode || !sessionAlias || !isSessionHydrated) return undefined;

        if (consumeSuppressedSessionWrite(suppressNextSessionWriteRef)) {
            return undefined;
        }

        const timer = setTimeout(() => {
            lastOutboundWriteAtRef.current = Date.now();
            sessionApi.updateVirtualGameState(sessionCode, {
                updatedByAlias: sessionAlias,
                game: 'trio',
                snapshot: localSessionSnapshot,
            }).then(() => (
                emitRealtimeEvent(
                    'session-updated',
                    { code: sessionCode, action: 'virtual-game-state', game: 'trio' },
                    sessionCode
                ).catch(() => null)
            )).catch(() => null);
        }, 180);
        return () => clearTimeout(timer);
    }, [
        isSessionMode,
        emitRealtimeEvent,
        sessionAlias,
        sessionCode,
        isSessionHydrated,
        localSessionSnapshot,
    ]);

    useEffect(() => {
        if (!players.length) return;
        storageManager.saveTemporaryData(
            TRIO_GAME_CACHE_KEY,
            stampSessionScopedGameState({
                setup: {
                    players,
                    targetTrios,
                    difficulty,
                    humanPlayerName,
                    humanPlayers: resolvedHumanPlayers
                },
                state,
                currentPlayerIndex,
                reveals,
                status,
                logEntries,
                winner
            }, isSessionMode, sessionCode),
            8
        );
    }, [
        players,
        targetTrios,
        difficulty,
        humanPlayerName,
        resolvedHumanPlayers,
        state,
        currentPlayerIndex,
        reveals,
        status,
        logEntries,
        winner,
        isSessionMode,
        sessionCode
    ]);

    useEffect(() => () => {
        cpuTimersRef.current.forEach((timer) => clearTimeout(timer));
        cpuTimersRef.current = [];
    }, []);

    useEffect(() => {
        revealsRef.current = reveals;
    }, [reveals]);

    const appendLog = (actor, text) => {
        setLogEntries((prev) => {
            const lastEntry = prev[prev.length - 1];
            const sameAsLast = Boolean(lastEntry)
                && String(lastEntry.text || '') === String(text || '')
                && String(lastEntry.actor || '') === String(actor || '');
            if (sameAsLast) {
                return prev;
            }
            return [
                ...prev.slice(-49),
                { id: `${Date.now()}-${prev.length}`, actor, text },
            ];
        });
    };

    const trioValueSort = (left, right) => {
        const leftIsNumber = typeof left === 'number';
        const rightIsNumber = typeof right === 'number';
        if (leftIsNumber && rightIsNumber) return left - right;
        if (leftIsNumber) return -1;
        if (rightIsNumber) return 1;
        return String(left).localeCompare(String(right));
    };

    const getCollectedTrios = (playerName) => {
        const fromState = state?.collectedTrios?.[playerName];
        if (Array.isArray(fromState)) return fromState;
        const fallbackCount = Math.max(0, Number(state?.scores?.[playerName] || 0));
        return Array.from({ length: fallbackCount }, () => '?');
    };

    const formatCollectedTrios = (playerName) => {
        const values = getCollectedTrios(playerName);
        return values.length ? values.join(', ') : 'none';
    };

    const advanceTurn = () => {
        if (winner) return;
        setCurrentPlayerIndex((value) => (value + 1) % state.hands.length);
    };

    const clearTurn = (nextStatus, moveTurn = true) => {
        revealsRef.current = [];
        setReveals([]);
        setStatus(nextStatus);
        if (moveTurn) {
            advanceTurn();
        }
    };

    const revealKey = (reveal) => {
        if (reveal.source === 'table') {
            return `table-${reveal.tableIndex}`;
        }
        return `player-${reveal.playerIndex}-${reveal.cardIndex}`;
    };

    const alreadyRevealed = (candidate) => revealsRef.current.some((item) => revealKey(item) === revealKey(candidate));

    const removeRevealedCards = (currentState, turnReveals) => {
        const nextHands = currentState.hands.map((hand) => ({ ...hand, cards: [...hand.cards] }));
        const nextTable = [...currentState.tableCards];

        const groupedPlayerRemovals = {};
        turnReveals
            .filter((reveal) => reveal.source === 'player')
            .forEach((reveal) => {
                if (!groupedPlayerRemovals[reveal.playerIndex]) {
                    groupedPlayerRemovals[reveal.playerIndex] = [];
                }
                groupedPlayerRemovals[reveal.playerIndex].push(reveal.cardIndex);
            });

        Object.entries(groupedPlayerRemovals).forEach(([playerIndexValue, indexes]) => {
            const playerIndex = Number(playerIndexValue);
            indexes.sort((left, right) => right - left).forEach((index) => {
                nextHands[playerIndex].cards.splice(index, 1);
            });
        });

        turnReveals
            .filter((reveal) => reveal.source === 'table')
            .map((reveal) => reveal.tableIndex)
            .sort((left, right) => right - left)
            .forEach((index) => {
                nextTable.splice(index, 1);
            });

        return { nextHands, nextTable };
    };

    const scheduleTurnAdvance = (delayMs = 900) => {
        setIsCpuActing(true);
        const timer = setTimeout(() => {
            clearTurn('Reveal two cards. Match to continue.');
            setIsCpuActing(false);
        }, delayMs);
        cpuTimersRef.current.push(timer);
    };

    const evaluateReveals = (nextReveals) => {
        if (nextReveals.length === 2) {
            const [left, right] = nextReveals;
            if (left.value === right.value) {
                setStatus('Match! Reveal one more card for a trio.');
                appendLog(currentPlayer, `Found a pair of ${left.value}.`);
            } else {
                setStatus('No match. Next player.');
                appendLog(currentPlayer, `Missed with ${left.value} and ${right.value}.`);
                scheduleTurnAdvance();
            }
            return;
        }

        if (nextReveals.length === 3) {
            const [first, second, third] = nextReveals;
            const isTrio = first.value === second.value && second.value === third.value;
            if (!isTrio) {
                setStatus('Third card missed. Next player.');
                appendLog(currentPlayer, `Missed trio with ${first.value}/${second.value}/${third.value}.`);
                scheduleTurnAdvance();
                return;
            }

            const { nextHands, nextTable } = removeRevealedCards(state, nextReveals);
            const previousCollected = Array.isArray(state.collectedTrios?.[currentPlayer])
                ? state.collectedTrios[currentPlayer]
                : Array.from({ length: Math.max(0, Number(state.scores?.[currentPlayer] || 0)) }, () => '?');
            const updatedCollectedTrios = {
                ...(state.collectedTrios || {}),
                [currentPlayer]: [...previousCollected, first.value].sort(trioValueSort),
            };
            const updatedScores = {
                ...state.scores,
                [currentPlayer]: updatedCollectedTrios[currentPlayer].length,
            };

            const playerScore = updatedCollectedTrios[currentPlayer].length;
            appendLog(currentPlayer, `Collected trio ${first.value}.`);
            setStatus(`${currentPlayer} collected trio ${first.value}!`);
            revealsRef.current = [];
            setReveals([]);
            setState({
                ...state,
                hands: nextHands,
                tableCards: nextTable,
                collectedTrios: updatedCollectedTrios,
                scores: updatedScores,
            });

            if (playerScore >= state.targetTrios) {
                setWinner(currentPlayer);
                appendLog(currentPlayer, 'Reached target trios and won.');
                return;
            }

            if (nextHands.every((hand) => hand.cards.length === 0) && nextTable.length === 0) {
                const top = Object.entries(updatedCollectedTrios)
                    .map(([player, trios]) => [player, Array.isArray(trios) ? trios.length : 0])
                    .sort((left, right) => right[1] - left[1])[0];
                setWinner(top?.[0] || currentPlayer);
                return;
            }

            scheduleTurnAdvance();
        }
    };

    const revealCandidate = (candidate) => {
        if (winner) return;
        if (alreadyRevealed(candidate)) return;

        const nextReveals = [...revealsRef.current, candidate];
        revealsRef.current = nextReveals;
        setReveals(nextReveals);
        evaluateReveals(nextReveals);
    };

    const resolvePlayerRevealIndex = (playerIndex, side) => {
        const hand = state.hands[playerIndex]?.cards || [];
        if (!hand.length) return -1;

        const revealedIndexes = new Set(
            revealsRef.current
                .filter((reveal) => reveal.source === 'player' && reveal.playerIndex === playerIndex)
                .map((reveal) => reveal.cardIndex)
        );

        if (revealedIndexes.size >= hand.length) return -1;

        if (side === 'high') {
            for (let index = hand.length - 1; index >= 0; index -= 1) {
                if (!revealedIndexes.has(index)) {
                    return index;
                }
            }
            return -1;
        }

        for (let index = 0; index < hand.length; index += 1) {
            if (!revealedIndexes.has(index)) {
                return index;
            }
        }
        return -1;
    };

    const revealPlayerCard = (playerIndex, cardIndex) => {
        if (!canInteractTurn || isCpuActing || winner) return;

        const hand = state.hands[playerIndex];
        const requestedSide = cardIndex === 0 ? 'low' : 'high';
        const resolvedCardIndex = resolvePlayerRevealIndex(playerIndex, requestedSide);
        if (resolvedCardIndex < 0) return;
        const value = hand.cards[resolvedCardIndex];
        if (value === undefined) return;

        revealCandidate({
            source: 'player',
            playerIndex,
            cardIndex: resolvedCardIndex,
            side: requestedSide,
            value
        });
    };

    const revealTableCard = (tableIndex) => {
        if (!canInteractTurn || isCpuActing || winner) return;

        const value = state.tableCards[tableIndex];
        if (value === undefined) return;

        revealCandidate({ source: 'table', tableIndex, value });
    };

    const getPlayerChoices = (playerIndex, showValues = false) => {
        const hand = state.hands[playerIndex]?.cards || [];
        if (hand.length === 0) return [];
        if (hand.length === 1) {
            return [{ label: showValues ? `Only (${hand[0]})` : 'Only Card', cardIndex: 0 }];
        }
        return [
            { label: showValues ? `Low (${hand[0]})` : 'Low Card', cardIndex: 0 },
            { label: showValues ? `High (${hand[hand.length - 1]})` : 'High Card', cardIndex: hand.length - 1 },
        ];
    };

    const chooseCpuRevealSequence = () => {
        const profile = TRIO_CPU_DIFFICULTY_PROFILES[difficulty] || TRIO_CPU_DIFFICULTY_PROFILES.easy;
        const options = [];

        state.hands.forEach((hand, playerIndex) => {
            if (!hand.cards.length) return;
            options.push({ source: 'player', playerIndex, cardIndex: 0, value: hand.cards[0] });
            if (hand.cards.length > 1) {
                options.push({
                    source: 'player',
                    playerIndex,
                    cardIndex: hand.cards.length - 1,
                    value: hand.cards[hand.cards.length - 1]
                });
            }
        });

        state.tableCards.forEach((value, tableIndex) => {
            options.push({ source: 'table', tableIndex, value });
        });

        const deduped = Array.from(new Map(options.map((option) => [revealKey(option), option])).values());
        if (deduped.length < 2) {
            return deduped;
        }

        const pickRandom = (list) => list[Math.floor(Math.random() * list.length)];
        const pickDistinct = (list, usedKeys) => {
            const filtered = list.filter((item) => !usedKeys.has(revealKey(item)));
            if (!filtered.length) return null;
            return pickRandom(filtered);
        };

        const groups = deduped.reduce((acc, option) => {
            const valueKey = String(option.value);
            if (!acc[valueKey]) acc[valueKey] = [];
            acc[valueKey].push(option);
            return acc;
        }, {});

        const valuesWithPair = Object.entries(groups).filter(([, items]) => items.length >= 2);
        const valuesWithTrio = Object.entries(groups)
            .filter(([, items]) => items.length >= 3)
            .sort((left, right) => right[1].length - left[1].length);

        let first = null;
        let second = null;

        const shouldSeekPair = Math.random() < profile.pairSeekChance;
        if (valuesWithTrio.length && shouldSeekPair) {
            first = valuesWithTrio[0][1][0];
            second = valuesWithTrio[0][1][1];
        } else if (valuesWithPair.length && shouldSeekPair) {
            const sortedPairs = [...valuesWithPair].sort((left, right) => right[1].length - left[1].length);
            first = sortedPairs[0][1][0];
            second = sortedPairs[0][1][1];
        } else {
            first = pickRandom(deduped);
            second = pickDistinct(deduped, new Set([revealKey(first)]));
        }

        if (!first || !second) {
            return deduped.slice(0, 2);
        }

        const sequence = [first, second];
        const usedKeys = new Set(sequence.map((item) => revealKey(item)));

        if (first.value === second.value) {
            const thirdOptions = deduped.filter(
                (option) => option.value === first.value && !usedKeys.has(revealKey(option))
            );
            const remainingOptions = deduped.filter((option) => !usedKeys.has(revealKey(option)));
            const shouldChaseTrio = Math.random() < profile.trioChaseChance;

            if (shouldChaseTrio && thirdOptions.length) {
                sequence.push(pickRandom(thirdOptions));
            } else if (remainingOptions.length) {
                // Always attempt a 3rd reveal when a pair is found so CPU turn can resolve.
                sequence.push(pickRandom(remainingOptions));
            }
        }

        return sequence;
    };

    const getCpuTimingProfile = () => {
        const profile = TRIO_CPU_DIFFICULTY_PROFILES[difficulty] || TRIO_CPU_DIFFICULTY_PROFILES.easy;
        return {
            thinkMs: randomBetween(profile.thinkMinMs, profile.thinkMaxMs),
            revealStepMs: randomBetween(profile.revealMinMs, profile.revealMaxMs),
        };
    };

    useEffect(() => {
        const canRunCpuTurns = !isSessionMode || isSessionHost;
        if (!isSessionHydrated || !canRunCpuTurns || !isCpuTurn || isCpuActing || reveals.length > 0 || winner) {
            return;
        }

        const { thinkMs, revealStepMs } = getCpuTimingProfile();
        const sequence = chooseCpuRevealSequence();

        if (!sequence.length) {
            setStatus(`${currentPlayer} has no reveal options. Next player.`);
            const emptyTimer = setTimeout(() => clearTurn('Reveal two cards. Match to continue.'), 700);
            cpuTimersRef.current.push(emptyTimer);
            return;
        }

        setIsCpuActing(true);
        setStatus(`${currentPlayer} is thinking...`);

        const startTimer = setTimeout(() => {
            sequence.forEach((candidate, index) => {
                const revealTimer = setTimeout(() => {
                    revealCandidate(candidate);
                }, revealStepMs * (index + 1));
                cpuTimersRef.current.push(revealTimer);
            });

            const releaseTimer = setTimeout(() => {
                setIsCpuActing(false);
                if (sequence.length < 2) {
                    // Guard against dead turns near endgame when only one reveal is possible.
                    clearTurn('Reveal two cards. Match to continue.');
                    return;
                }
                if (sequence.length === 2 && sequence[0].value === sequence[1].value) {
                    // No third reveal available; force turn progression instead of stalling on pair state.
                    clearTurn('Reveal two cards. Match to continue.');
                }
            }, revealStepMs * sequence.length + 1200);
            cpuTimersRef.current.push(releaseTimer);
        }, thinkMs);

        cpuTimersRef.current.push(startTimer);
    }, [isCpuTurn, isCpuActing, reveals.length, winner, difficulty, currentPlayer, isSessionHost, isSessionMode, isSessionHydrated]);

    const revealedTableIndexes = useMemo(() => new Set(
        reveals
            .filter((reveal) => reveal.source === 'table')
            .map((reveal) => reveal.tableIndex)
    ), [reveals]);
    const requestedThisTurn = useMemo(() => (
        reveals.map((reveal, index) => {
            if (reveal.source === 'table') {
                return {
                    key: `table-${index}-${reveal.tableIndex}`,
                    label: `${index + 1}. Table #${reveal.tableIndex + 1}`,
                    value: reveal.value
                };
            }

            const playerName = state.hands[reveal.playerIndex]?.name || 'Player';
            const positionLabel = reveal.side === 'high' ? 'High' : 'Low';
            return {
                key: `player-${index}-${reveal.playerIndex}-${reveal.cardIndex}`,
                label: `${index + 1}. ${playerName} ${positionLabel}`,
                value: reveal.value
            };
        })
    ), [reveals, state.hands]);
    const sortedScores = [...players]
        .map((player) => ({ player, trios: getCollectedTrios(player), score: getCollectedTrios(player).length }))
        .sort((left, right) => right.score - left.score);
    const latestPlayerRevealByIndex = useMemo(() => {
        const indexMap = {};
        reveals.forEach((reveal) => {
            if (reveal.source === 'player') {
                indexMap[reveal.playerIndex] = reveal;
            }
        });
        return indexMap;
    }, [reveals]);
    const handEntries = state.hands.map((hand, playerIndex) => ({ hand, playerIndex }));
    const viewerEntry = handEntries.find(
        ({ hand }) => String(hand.name).toLowerCase() === String(visibleHandPlayerName).toLowerCase()
    ) || handEntries[0];
    const opponentEntries = handEntries.filter(({ hand }) => hand.name !== viewerEntry?.hand?.name);
    const opponentDesktopSlots = [
        { top: '4%', left: '50%', transform: 'translateX(-50%)' },
        { top: '14%', left: '3%', transform: 'none' },
        { top: '14%', right: '3%', transform: 'none' },
        { top: '30%', left: '3%', transform: 'none' },
        { top: '30%', right: '3%', transform: 'none' },
    ];

    const handleEndMatch = async () => {
        if (isSessionMode && !isSessionHost) {
            return;
        }
        if (isSessionMode && isSessionHost && sessionCode) {
            try {
                await sessionApi.endVirtualGame(sessionCode, { endedByAlias: sessionAlias });
                emitRealtimeEvent(
                    'session-updated',
                    { code: sessionCode, action: 'virtual-game-ended', game: 'trio' },
                    sessionCode
                ).catch(() => null);
            } catch {
                // no-op
            }
        }

        storageManager.removeTemporaryData(TRIO_GAME_CACHE_KEY);
        navigate('/trio-setup');
    };

    if (isSessionMode && !isSessionHydrated) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: theme.bgMedium,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.textPrimary
            }}>
                Loading session state...
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            padding: '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        }}>
            <BackButton to="/trio-setup" preserveState />
            <HelpButton helpText="Reveal low/high cards to find matches and collect trios. Difficulty only affects bot turns." />
            <SettingsButton />
            <SocialButton />

            <div style={{ marginTop: isMobile ? '45px' : '20px', textAlign: 'center' }}>
                <h1 className="page-title">TRIO</h1>
            </div>

            <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', display: 'grid', gap: '10px' }}>
                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                }}>
                    <div><strong>Current Turn:</strong> {winner ? 'Game Over' : currentPlayer}{canInteractTurn ? ' (You)' : ''}</div>
                    <div><strong>Target Trios:</strong> {targetTrios}</div>
                    <div><strong>Difficulty:</strong> {hasBots ? difficulty.toUpperCase() : 'N/A (No Bots)'}</div>
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '10px'
                }}>
                    <div style={{ marginBottom: '6px', color: theme.textSecondary, display: 'grid', gap: '6px' }}>
                        <div>Revealed this turn:</div>
                        {reveals.length > 0 ? (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {reveals.map((reveal, index) => (
                                    <TrioNumberCard
                                        key={`revealed-turn-${index}-${reveal.value}`}
                                        value={reveal.value}
                                        theme={theme}
                                        compact
                                        disabled
                                    />
                                ))}
                            </div>
                        ) : (
                            <div>none</div>
                        )}
                    </div>
                    {isSessionMode && <div style={{ marginBottom: '6px', color: theme.textSecondary }}>Session mode: only your hand is visible.</div>}
                    <div>{winner ? `${winner} wins!` : status}</div>
                    {requestedThisTurn.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'grid', gap: '4px', color: theme.textSecondary }}>
                            {requestedThisTurn.map((item) => (
                                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{item.label}</span>
                                    <TrioNumberCard
                                        value={item.value}
                                        theme={theme}
                                        compact
                                        disabled
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '10px',
                    display: 'grid',
                    gap: '10px'
                }}>
                    <div style={{ color: theme.textSecondary }}>Board Layout</div>

                    {isMobile ? (
                        <div style={{ display: 'grid', gap: '10px' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
                                gap: '8px'
                            }}>
                                {opponentEntries.map(({ hand, playerIndex }) => {
                                    const latestReveal = latestPlayerRevealByIndex[playerIndex];
                                    const latestRevealLabel = latestReveal
                                        ? (hand.cards.length <= 1 ? 'Only' : (latestReveal.side === 'high' ? 'High' : 'Low'))
                                        : null;
                                    return (
                                        <div
                                            key={`mobile-opponent-${hand.name}`}
                                            style={{
                                                backgroundColor: theme.bgMedium,
                                                borderRadius: '8px',
                                                border: `1px solid ${currentPlayerIndex === playerIndex ? theme.accent : theme.secondary}`,
                                                padding: '8px',
                                                display: 'grid',
                                                gap: '6px'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                                <strong style={{ fontSize: '0.9rem' }}>{hand.name}</strong>
                                                <span style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
                                                    {hand.cards.length} cards
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                                <div style={{ position: 'relative', width: '56px', height: '62px' }}>
                                                    {Array.from({ length: Math.max(1, Math.min(3, hand.cards.length)) }).map((_, stackIndex) => (
                                                        <div
                                                            key={`${hand.name}-mobile-stack-${stackIndex}`}
                                                            style={{ position: 'absolute', left: `${stackIndex * 4}px`, top: `${stackIndex * 3}px` }}
                                                        >
                                                            <TrioNumberCard
                                                                value={12}
                                                                hidden
                                                                theme={theme}
                                                                compact
                                                                disabled
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{ minHeight: '56px', display: 'flex', alignItems: 'center', gap: '6px', minWidth: '74px' }}>
                                                    {latestReveal ? (
                                                        <>
                                                            <TrioNumberCard
                                                                value={latestReveal.value}
                                                                theme={theme}
                                                                compact
                                                                disabled
                                                            />
                                                            <span style={{ color: theme.textSecondary, fontSize: '0.76rem' }}>{latestRevealLabel}</span>
                                                        </>
                                                    ) : (
                                                        <span style={{ color: theme.textSecondary, fontSize: '0.76rem' }}>No reveal</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                {getPlayerChoices(playerIndex, false).map((choice) => (
                                                    <button
                                                        key={`${hand.name}-mobile-${choice.label}`}
                                                        onClick={() => revealPlayerCard(playerIndex, choice.cardIndex)}
                                                        disabled={!canInteractTurn || Boolean(winner) || isCpuActing}
                                                        style={{
                                                            ...getButtonStyle(theme, {
                                                                variant: currentPlayerIndex === playerIndex ? 'accent' : 'secondary',
                                                                disabled: !canInteractTurn || Boolean(winner) || isCpuActing,
                                                                padding: '5px 7px'
                                                            }),
                                                            fontSize: '0.74rem',
                                                            minWidth: '64px'
                                                        }}
                                                    >
                                                        {choice.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{
                                backgroundColor: theme.bgMedium,
                                borderRadius: '8px',
                                border: `1px solid ${theme.secondary}`,
                                padding: '8px',
                                display: 'grid',
                                gap: '6px'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setIsMobileTableModalOpen(true)}
                                    style={{
                                        ...getButtonStyle(theme, {
                                            variant: 'secondary',
                                            padding: '7px 9px'
                                        }),
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '0.82rem'
                                    }}
                                >
                                    <span>Table Cards ({state.tableCards.length})</span>
                                    <span>Open</span>
                                </button>
                            </div>

                            {viewerEntry && (
                                <div style={{
                                    backgroundColor: theme.bgMedium,
                                    borderRadius: '8px',
                                    border: `2px solid ${currentPlayerIndex === viewerEntry.playerIndex ? theme.accent : theme.secondary}`,
                                    padding: '10px',
                                    display: 'grid',
                                    gap: '8px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                        <strong>{viewerEntry.hand.name} (You)</strong>
                                        <span style={{ color: theme.textSecondary }}>
                                            {viewerEntry.hand.cards.length} cards • Trios: {formatCollectedTrios(viewerEntry.hand.name)}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {viewerEntry.hand.cards.map((cardValue, cardIndex) => (
                                            <TrioNumberCard
                                                key={`${viewerEntry.hand.name}-mobile-card-${cardIndex}-${cardValue}`}
                                                value={cardValue}
                                                theme={theme}
                                                disabled
                                            />
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {getPlayerChoices(viewerEntry.playerIndex, true).map((choice) => (
                                            <button
                                                key={`${viewerEntry.hand.name}-mobile-choice-${choice.label}`}
                                                onClick={() => revealPlayerCard(viewerEntry.playerIndex, choice.cardIndex)}
                                                disabled={!canInteractTurn || Boolean(winner) || isCpuActing}
                                                style={{
                                                    ...getButtonStyle(theme, {
                                                        variant: currentPlayerIndex === viewerEntry.playerIndex ? 'accent' : 'secondary',
                                                        disabled: !canInteractTurn || Boolean(winner) || isCpuActing,
                                                        padding: '7px 9px'
                                                    }),
                                                    fontSize: '0.82rem',
                                                    minWidth: '74px'
                                                }}
                                            >
                                                {choice.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isMobileTableModalOpen && (
                                <div
                                    onClick={() => setIsMobileTableModalOpen(false)}
                                    style={{
                                        position: 'fixed',
                                        inset: 0,
                                        backgroundColor: 'rgba(0, 0, 0, 0.72)',
                                        zIndex: 1400,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '16px'
                                    }}
                                >
                                    <div
                                        onClick={(event) => event.stopPropagation()}
                                        style={{
                                            width: '100%',
                                            maxWidth: '360px',
                                            maxHeight: '78vh',
                                            overflowY: 'auto',
                                            backgroundColor: theme.bgDark,
                                            border: `2px solid ${theme.secondary}`,
                                            borderRadius: '12px',
                                            padding: '10px',
                                            display: 'grid',
                                            gap: '10px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <strong>Table Cards ({state.tableCards.length})</strong>
                                            <button
                                                type="button"
                                                onClick={() => setIsMobileTableModalOpen(false)}
                                                style={{
                                                    ...getButtonStyle(theme, {
                                                        variant: 'secondary',
                                                        padding: '4px 8px'
                                                    }),
                                                    fontSize: '0.78rem',
                                                    minWidth: '62px'
                                                }}
                                            >
                                                Close
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                                            {state.tableCards.map((value, tableIndex) => (
                                                <TrioNumberCard
                                                    key={`mobile-modal-table-${tableIndex}`}
                                                    value={value}
                                                    hidden={!revealedTableIndexes.has(tableIndex)}
                                                    theme={theme}
                                                    onClick={() => revealTableCard(tableIndex)}
                                                    disabled={!canInteractTurn || Boolean(winner) || isCpuActing}
                                                />
                                            ))}
                                            {state.tableCards.length === 0 && (
                                                <span style={{ color: theme.textSecondary }}>No table cards.</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            position: 'relative',
                            minHeight: '600px',
                            backgroundColor: theme.bgMedium,
                            borderRadius: '10px',
                            border: `1px solid ${theme.secondary}`,
                            overflow: 'hidden'
                        }}>
                            {opponentEntries.map(({ hand, playerIndex }, index) => {
                                const latestReveal = latestPlayerRevealByIndex[playerIndex];
                                const latestRevealLabel = latestReveal
                                    ? (hand.cards.length <= 1 ? 'Only' : (latestReveal.side === 'high' ? 'High' : 'Low'))
                                    : null;
                                const slot = opponentDesktopSlots[index % opponentDesktopSlots.length];
                                return (
                                    <div
                                        key={`opponent-${hand.name}`}
                                        style={{
                                            position: 'absolute',
                                            ...slot,
                                            width: 'min(185px, 22%)',
                                            backgroundColor: theme.bgDark,
                                            borderRadius: '8px',
                                            border: `1px solid ${currentPlayerIndex === playerIndex ? theme.accent : theme.secondary}`,
                                            padding: '8px',
                                            display: 'grid',
                                            gap: '6px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                            <strong style={{ fontSize: '0.88rem' }}>{hand.name}</strong>
                                            <span style={{ fontSize: '0.78rem', color: theme.textSecondary }}>
                                                {hand.cards.length} cards
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                            <div style={{ position: 'relative', width: '56px', height: '62px' }}>
                                                {Array.from({ length: Math.max(1, Math.min(3, hand.cards.length)) }).map((_, stackIndex) => (
                                                    <div
                                                        key={`${hand.name}-stack-${stackIndex}`}
                                                        style={{ position: 'absolute', left: `${stackIndex * 4}px`, top: `${stackIndex * 3}px` }}
                                                    >
                                                        <TrioNumberCard
                                                            value={12}
                                                            hidden
                                                            theme={theme}
                                                            compact
                                                            disabled
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{ minHeight: '56px', minWidth: '70px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {latestReveal ? (
                                                    <>
                                                        <TrioNumberCard
                                                            value={latestReveal.value}
                                                            theme={theme}
                                                            compact
                                                            disabled
                                                        />
                                                        <span style={{ color: theme.textSecondary, fontSize: '0.75rem' }}>{latestRevealLabel}</span>
                                                    </>
                                                ) : (
                                                    <span style={{ color: theme.textSecondary, fontSize: '0.75rem' }}>No reveal</span>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                            {getPlayerChoices(playerIndex, false).map((choice) => (
                                                <button
                                                    key={`${hand.name}-${choice.label}`}
                                                    onClick={() => revealPlayerCard(playerIndex, choice.cardIndex)}
                                                    disabled={!canInteractTurn || Boolean(winner) || isCpuActing}
                                                    style={{
                                                        ...getButtonStyle(theme, {
                                                            variant: currentPlayerIndex === playerIndex ? 'accent' : 'secondary',
                                                            disabled: !canInteractTurn || Boolean(winner) || isCpuActing,
                                                            padding: '5px 8px'
                                                        }),
                                                        fontSize: '0.76rem'
                                                    }}
                                                >
                                                    {choice.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            <div style={{
                                position: 'absolute',
                                left: '50%',
                                top: '56%',
                                transform: 'translate(-50%, -50%)',
                                width: 'min(440px, 54%)',
                                backgroundColor: theme.bgDark,
                                borderRadius: '8px',
                                border: `1px solid ${theme.secondary}`,
                                padding: '10px',
                                display: 'grid',
                                gap: '8px'
                            }}>
                                <div style={{ color: theme.textSecondary, textAlign: 'center' }}>
                                    Table Cards ({state.tableCards.length})
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    {state.tableCards.map((value, tableIndex) => (
                                        <TrioNumberCard
                                            key={`table-${tableIndex}`}
                                            value={value}
                                            hidden={!revealedTableIndexes.has(tableIndex)}
                                            theme={theme}
                                            onClick={() => revealTableCard(tableIndex)}
                                            disabled={!canInteractTurn || Boolean(winner) || isCpuActing}
                                        />
                                    ))}
                                    {state.tableCards.length === 0 && (
                                        <span style={{ color: theme.textSecondary }}>No table cards.</span>
                                    )}
                                </div>
                            </div>

                            {viewerEntry && (
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: '10px',
                                    transform: 'translateX(-50%)',
                                    width: 'min(760px, 96%)',
                                    backgroundColor: theme.bgDark,
                                    borderRadius: '10px',
                                    border: `2px solid ${currentPlayerIndex === viewerEntry.playerIndex ? theme.accent : theme.secondary}`,
                                    padding: '10px',
                                    display: 'grid',
                                    gap: '8px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                        <strong>
                                            {viewerEntry.hand.name}
                                            {isSessionMode ? ' (You)' : (isMultiHumanMode ? ' (Current)' : ' (You)')}
                                        </strong>
                                        <span style={{ color: theme.textSecondary }}>
                                            {viewerEntry.hand.cards.length} cards • Trios: {formatCollectedTrios(viewerEntry.hand.name)}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {viewerEntry.hand.cards.map((cardValue, cardIndex) => (
                                            <TrioNumberCard
                                                key={`${viewerEntry.hand.name}-card-${cardIndex}-${cardValue}`}
                                                value={cardValue}
                                                theme={theme}
                                                disabled
                                            />
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {getPlayerChoices(viewerEntry.playerIndex, true).map((choice) => (
                                            <button
                                                key={`${viewerEntry.hand.name}-choice-${choice.label}`}
                                                onClick={() => revealPlayerCard(viewerEntry.playerIndex, choice.cardIndex)}
                                                disabled={!canInteractTurn || Boolean(winner) || isCpuActing}
                                                style={{
                                                    ...getButtonStyle(theme, {
                                                        variant: currentPlayerIndex === viewerEntry.playerIndex ? 'accent' : 'secondary',
                                                        disabled: !canInteractTurn || Boolean(winner) || isCpuActing,
                                                        padding: '8px 10px'
                                                    }),
                                                }}
                                            >
                                                {choice.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '10px'
                }}>
                    <div style={{ marginBottom: '8px', color: theme.textSecondary }}>Scoreboard</div>
                    <div style={{ display: 'grid', gap: '6px' }}>
                        {sortedScores.map(({ player, trios, score }) => (
                            <div key={player} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{player}</span>
                                <strong>{trios.length ? trios.join(', ') : `none (${score})`}</strong>
                            </div>
                        ))}
                    </div>
                </div>

                <GameLog title="Trio Log" entries={logEntries} maxHeight={140} />

                <button
                    onClick={handleEndMatch}
                    disabled={isSessionMode && !isSessionHost}
                    style={{
                        ...getButtonStyle(theme, {
                            variant: 'accent',
                            padding: '12px 16px',
                            disabled: isSessionMode && !isSessionHost,
                        }),
                    }}
                >
                    {isSessionMode ? (isSessionHost ? 'End Session Match' : 'Host Ends Match') : 'New Trio Match'}
                </button>
            </div>
        </div>
    );
}

export default TrioGame;

